import { PureComponent } from 'react';

// Material UI imports
import {
  Typography,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Grid,
  InputAdornment,
  withStyles
} from '@material-ui/core';

// Material UI icons
import SearchIcon from '@mui/icons-material/SearchRounded';

// Responsive dialog
import ResponsiveDialog from 'components/ResponsiveDialog';

// Autocomplete component
import { Autocomplete } from '@material-ui/lab';

// Snackbar provider
import { withSnackbar } from 'notistack';

// To clean up exports
import compose from 'recompose/compose';

// To validate the form input
import validate from 'validate.js';

import {
  getMaterialLevelInfo,
  getMaterialLevelInfoPTE
} from 'services/materials';

// Login schema
import schema from './schema';

// Component styles
import styles from './styles';

class BookingDialog extends PureComponent {
  constructor(props) {
    super(props);

    // Initialize the state
    this.state = {
      open: false,
      isValid: false,
      values: {},
      touched: {},
      errors: {},
      material: '',
      materialOptions: undefined,
      course: '',
      courseOptions: undefined
    };

    // Bind functions
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.confirm = this.confirm.bind(this);
    this.onMaterialChange = this.onMaterialChange.bind(this);

    // Bind event handlers
    this.handleChange = this.handleChange.bind(this);

    // Create the events object
    this.events = {
      close: this.close,
      confirm: this.confirm
    };

    // Set the signal to true to allow for state updates
    this.ready = true;
  }

  async componentDidMount() {
    // Grab the topic options from the state
    const { materialOptions } = this.state;
    const { enqueueSnackbar } = this.props;

    // If we've already retrived the material options, return
    if (materialOptions && materialOptions.length > 0) return;

    // Grab the materials endpoint from the props
    const { auth, student } = this.props;

    // Wrap in a try-catch to handle any errors
    try {
      // Retrieve the materials from the API
      const materials = await auth.api.materials.getMaterials();

      // Update the state with the materials (don't auto-select, let user choose)
      if (this.ready) {
        this.setState({
          materialOptions: materials,
          material: '',
          course: '',
          courseOptions: undefined
        });
      }
    } catch (error) {
      // Log the error to the console
      console.error(error);

      // Show an error snackbar
      enqueueSnackbar(
        'There was an error when retriving the lesson material options for your booking.',
        {
          variant: 'error'
        }
      );
    }
  }

  componentWillUnmount() {
    // Set the ready flag to false to prevent any further state changes
    this.ready = false;
  }

  async onMaterialChange(material) {
    // Grab the materials endpoint from the props
    const { auth, enqueueSnackbar } = this.props;

    // Save a copy of the old material state
    const {
      material: oldMaterial,
      materialOptions: oldMaterialOptions,
      course: oldCourse,
      courseOptions: oldCourseOptions
    } = this.state;

    // Wrap in a try-catch to handle any errors
    try {
      // Update the state to begin loading
      if (this.ready) {
        this.setState({
          material,
          course: '',
          courseOptions: undefined,
          isValid: false,
          values: {
            ...this.state.values,
            topic: ''
          }
        });
      }

      // Retrieve the courses from the API
      const courses = await auth.api.materials.getCourses(material);

      // Update the state with the courses (don't auto-select, let user choose)
      if (this.ready) {
        this.setState({
          course: '',
          courseOptions: courses
        });
      }
    } catch (error) {
      // Log the error to the console
      console.error(error);

      // Reset the state
      if (this.ready) {
        this.setState({
          material: oldMaterial,
          materialOptions: oldMaterialOptions,
          course: oldCourse,
          courseOptions: oldCourseOptions
        });
      }

      // Show an error snackbar
      enqueueSnackbar('An error occured when loading the streams.', {
        variant: 'error'
      });
    }
  }

  // Create a open function to hook into in
  // the parent component
  open(timestamp, booking) {
    this.setState({
      values: {
        introduction: true,
        correctMistakes: true,
        topic: ''
      },
      timestamp,
      booking,
      open: true,
      isValid: false,
      course: '',
      courseOptions: undefined
    });
  }

  // Create a close function to hook into in
  // the parent component
  close() {
    this.setState({
      open: false
    });
  }

  confirm() {
    // Destructure the state
    const {
      isValid,
      values,
      booking,
      timestamp,
      material,
      course,
      materialOptions,
      courseOptions
    } = this.state;

    // Grab the onConfirm and enqueueSnackbar functions from thep rops
    const { onConfirm, enqueueSnackbar } = this.props;

    // If the form is valid, submit it
    if (isValid) {
      // Trigger the onConfirm function
      this.close();

      // Get the current material and course
      const currentMaterial = materialOptions.find(
        (entry) => entry.id === material
      );
      const currentCourse = courseOptions.find((entry) => entry.id === course);

      // Return the material
      onConfirm({
        values: {
          ...values,
          topic: {
            material: currentMaterial.id,
            course: currentCourse.id,
            lesson: values.topic.id,
            code: values.topic.code,
            name: values.topic.name
          }
        },
        booking,
        timestamp
      });
    } else {
      // Show an error snackbar
      enqueueSnackbar(
        'Please correctly fill out all details before submitting.',
        { variant: 'error' }
      );
    }
  }

  handleChange(event) {
    // Grab the values from the state
    const { values, touched } = this.state;

    // Get the newly changed values
    const newValues = {
      ...values,
      [event.target.name]:
        event.target.type === 'checkbox'
          ? event.target.checked
          : event.target.value
    };

    // Validate the state's values
    const errors = validate(newValues, schema);

    // Update the state
    this.setState({
      values: newValues,
      touched: {
        ...touched,
        [event.target.name]: true
      },
      isValid: !errors,
      errors: errors || {}
    });
  }

  hasError(field) {
    // Get the errors from the state
    const { touched, errors } = this.state;
    return !!(touched[field] && errors[field]);
  }

  render() {
    // Get the current from state
    const {
      values,
      errors,
      isValid,
      open,
      material,
      course,
      materialOptions,
      courseOptions
    } = this.state;

    // Retrieve out styles from the props
    const { classes, student, selectedBooking } = this.props;
    const isTutorPte = selectedBooking?.profile?.tutor?.isPte || false;

    // Find the current course
    const currentCourse =
      courseOptions && courseOptions.find((entry) => entry.id === course);
    const isA2 = ['AA2'].includes(currentCourse?.abbreviation);
    const subscription = student?.stripe?.subscription;
    const categories = subscription?.currentSubscription?.categories || [];

    // Check if free trial was activated OR if student has free trial tokens available
    // This allows students with refunded free trial tokens to attempt bookings
    const freeTrialActivatedFlag = student?.stripe?.freeTrialActivated === true;
    const hasFreeTrialTokens = (student?.data?.freeTrialToken || 0) > 0;
    // Allow booking if free trial was activated OR if they have refunded free trial tokens
    const freeTrialActivated = freeTrialActivatedFlag || hasFreeTrialTokens;
    const hasValidSubscription =
      subscription && subscription.currentSubscription && categories.length > 0;
    const ptePriceIds = [
      'price_1ScelVHns6zXPgc8TXmCFOwx',
      'price_1Sg0VXHns6zXPgc8UdS7MI03',
      'price_1R4HRwHns6zXPgc8A2KYJiq8', // A2 PTE
      'price_1R8FF8Hns6zXPgc89ZUG51jJ' // B2 PTE
    ];

    const workplacePriceIds = [
      'price_1RyXvuHns6zXPgc8RhXl2XKb',
      'price_1S2SmBHns6zXPgc8biFSc3Hz',
      'price_1SxjmmHns6zXPgc8eMMrbiHP', // New Workplace (AUD)
      // Generic plans that provide regular lessonTokens
      'price_1PdkHPHns6zXPgc8JW7NjtgR',
      'price_1PdkKqHns6zXPgc8eAJMwUL1',
      'price_1PdkPEHns6zXPgc8JwlYNrjS',
      'price_1PdkQGHns6zXPgc8bx9bBa2s',
      'price_1Pdp9gHns6zXPgc8gUTjvbFf',
      'price_1PdpAfHns6zXPgc8DUqaCMTx',
      'price_1PdpCjHns6zXPgc8KV9TigCs',
      'price_1PdpE6Hns6zXPgc8Vk5Jvk2d',
      'price_1P3FltHns6zXPgc8FHiiSk9l',
      'price_1PdpGAHns6zXPgc89F6A0d6P',
      'price_1PdpJ6Hns6zXPgc8rypUMVij',
      'price_1PdpK5Hns6zXPgc8c2cq82Ib'
    ];

    const hasPTESubscription =
      hasValidSubscription &&
      categories.some((cat) => {
        const isNotExpired =
          !cat.expiredAt || cat.expiredAt > Date.now() || cat.isPaymentMode;
        return ptePriceIds.includes(cat.priceId) && isNotExpired;
      });

    const haveWorkplaceSubscription =
      hasValidSubscription &&
      categories.some((cat) => {
        const isNotExpired =
          !cat.expiredAt || cat.expiredAt > Date.now() || cat.isPaymentMode;
        return workplacePriceIds.includes(cat.priceId) && isNotExpired;
      });

    return (
      <>
        <ResponsiveDialog
          title="Confirm Booking Details"
          events={this.events}
          open={open}
          confirmDisabled={!isValid || !materialOptions || !courseOptions}>
          <Typography className={classes.header} variant="h5">
            Topic
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <FormControl variant="filled" fullWidth>
                <InputLabel id="select-booking-material">Study Area</InputLabel>
                <Select
                  labelId="select-booking-material"
                  id="select-filled"
                  disabled={!materialOptions}
                  name="material"
                  value={material}
                  endAdornment={
                    !materialOptions && (
                      <CircularProgress
                        className={classes.adornment}
                        color="inherit"
                        size={20}
                      />
                    )
                  }
                  onChange={(event) => {
                    const selectedValue = event.target.value;
                    if (selectedValue) {
                      this.onMaterialChange(selectedValue);
                    } else {
                      // Reset if placeholder selected
                      this.setState({
                        material: '',
                        course: '',
                        courseOptions: undefined,
                        isValid: false,
                        values: {
                          ...this.state.values,
                          topic: ''
                        }
                      });
                    }
                  }}>
                  <MenuItem value="">
                    <em>Select your study area</em>
                  </MenuItem>
                  {materialOptions &&
                    materialOptions.map((materialItem) => {
                      // Check if this is PTE material and if user has active PTE subscription
                      const isPTE = materialItem.name === 'PTE';
                      console.log({
                        hasPTESubscription: !hasPTESubscription || !isTutorPte
                      });
                      return (
                        <MenuItem
                          key={materialItem.id}
                          value={materialItem.id}
                          disabled={
                            freeTrialActivated
                              ? isPTE && !isTutorPte // Free trial: can only do PTE with PTE tutors
                              : isPTE
                              ? !hasPTESubscription || !isTutorPte
                              : !haveWorkplaceSubscription
                          }>
                          {materialItem.name}{' '}
                          {isPTE &&
                            (!hasPTESubscription || !isTutorPte) &&
                            ' (Requires PTE Subscription and PTE Teacher)'}
                          {!isPTE &&
                            !haveWorkplaceSubscription &&
                            ' (Requires Workplace Subscription)'}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <FormControl variant="filled" fullWidth>
                <InputLabel id="select-booking-course">Stream</InputLabel>
                <Select
                  labelId="select-booking-course"
                  id="select-filled"
                  disabled={!material || !courseOptions}
                  name="course"
                  value={course}
                  onChange={(event) => {
                    const selectedValue = event.target.value;
                    this.setState({
                      course: selectedValue,
                      isValid: false,
                      values: {
                        ...this.state.values,
                        topic: selectedValue ? this.state.values.topic : ''
                      }
                    });
                  }}>
                  <MenuItem value="">
                    <em>Select your stream</em>
                  </MenuItem>
                  {courseOptions &&
                    courseOptions.map((courseItem) => {
                      return (
                        <MenuItem
                          key={courseItem.id}
                          value={courseItem.id}
                          disabled={
                            freeTrialActivated
                              ? false
                              : material == 'pte'
                              ? !hasPTESubscription
                              : false
                          }>
                          {courseItem.name}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Autocomplete
                disabled={!course}
                groupBy={(option) =>
                  `Level ${option.level} - ${
                    material == 'pte'
                      ? getMaterialLevelInfoPTE(isA2, option.level, true).name
                      : getMaterialLevelInfo(option.level).name
                  }`
                }
                autoHighlight
                options={
                  (currentCourse && currentCourse.lessons) || [
                    { name: 'Please select Stream first...', value: null }
                  ]
                }
                getOptionLabel={(opt) => opt && opt.name}
                value={values.topic || null}
                onInputChange={(e) => {
                  if (e !== null) {
                    this.handleChange({
                      target: {
                        name: 'topic',
                        value:
                          currentCourse.lessons[
                            parseInt(e.currentTarget.dataset.optionIndex, 10)
                          ]
                      }
                    });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    label="Lesson Topic"
                    name="topic"
                    variant="outlined"
                    error={this.hasError('topic')}
                    helperText={this.hasError('topic') ? errors.topic[0] : null}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: params.InputProps.endAdornment
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <div className={classes.section}>
            <Typography className={classes.header} variant="h5">
              Introduction
            </Typography>
            <RadioGroup
              aria-label="introduction"
              name="introduction"
              value={values.introduction}
              onChange={(event, value) => {
                this.handleChange({
                  target: {
                    name: 'introduction',
                    value: value === 'true'
                  }
                });
              }}>
              <FormControlLabel
                // eslint-disable-next-line react/jsx-boolean-value
                value={true}
                control={<Radio />}
                label="Yes, i'd like introductions at the beginning of the lesson."
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="No thanks, just start the lesson right away."
              />
            </RadioGroup>
          </div>
          <div className={classes.section}>
            <Typography className={classes.header} variant="h5">
              Correct Mistakes
            </Typography>
            <RadioGroup
              aria-label="correctMistakes"
              name="correctMistakes"
              value={values.correctMistakes}
              onChange={(event, value) => {
                this.handleChange({
                  target: {
                    name: 'correctMistakes',
                    value: value === 'true'
                  }
                });
              }}>
              <FormControlLabel
                // eslint-disable-next-line react/jsx-boolean-value
                value={true}
                control={<Radio />}
                label="Yes, please proactively correct my mistakes."
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="No thanks, let's just focus on the conversation."
              />
            </RadioGroup>
          </div>
          <div className={classes.section}>
            <Typography className={classes.header} variant="h5">
              Notes
            </Typography>
            <TextField
              multiline
              rows={4}
              error={this.hasError('notes')}
              fullWidth
              helperText={this.hasError('notes') ? errors.notes[0] : null}
              label="Notes"
              name="notes"
              onChange={this.handleChange}
              type="text"
              value={values.notes || ''}
              variant="outlined"
            />
          </div>
        </ResponsiveDialog>
      </>
    );
  }
}

export default compose(withStyles(styles), withSnackbar)(BookingDialog);
